/**
 * /financeiro/lancamentos — Lançamentos v2 (mockup Fase 5 V2.2).
 *
 * Layout .fin2: breadcrumb + título + ações; stat-cards (Entradas/Saídas/
 * Conciliados/A conciliar); toolbar (busca + Filtros + tabchips) + dtable +
 * paginação + bulk-bar de seleção. "Novo lançamento" e "Importar OFX" abrem
 * slide-overs (LancamentoPanels). Mesmos endpoints /api/lancamentos.
 *
 * As rotas legadas /criar e /importar-ofx redirecionam pra cá com ?novo / ?ofx
 * (auto-abrem o painel) — sem código duplicado nem deep link quebrado.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FiDownload, FiPlus, FiSearch } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import { useAuth } from "../../../context/AuthContext";
import {
  conciliarLancamento, confirmarLancamento, formatarReais,
  listarLancamentos,
} from "../../../services/financeiro";
import { PageContainer } from "../../../styles/shared";

import { ImportarOfxPanel, NovoLancamentoPanel } from "./LancamentoPanels";

const PAGE = 12;

function dataCurta(iso) {
  if (!iso) return "—";
  const [, m, d] = iso.split("-");
  return d && m ? `${d}/${m}` : iso;
}


export default function LancamentosLista() {
  const navigate = useNavigate();
  const { request, hasAllPermissions, isMaster } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busca, setBusca] = useState("");
  const [filtroTipo, setFiltroTipo] = useState(""); // "" | ENTRADA | SAIDA | CONCILIAR
  const [pagina, setPagina] = useState(0);
  const [selecionados, setSelecionados] = useState(() => new Set());

  const [painel, setPainel] = useState(null); // "novo" | "ofx" | null
  const [tipoNovo, setTipoNovo] = useState("ENTRADA");
  // M-TRA-8: lancamento agora pode ser corrigido pela tela.
  const [editando, setEditando] = useState(null);

  const podeLancar = isMaster || hasAllPermissions(["financeiro:lancar"]);
  const podeImportar = isMaster || hasAllPermissions(["financeiro:importar"]);
  const podeConciliar = isMaster || hasAllPermissions(["financeiro:conciliar"]);

  // Abertura dos painéis via query param (deep link das rotas legadas)
  useEffect(() => {
    if (searchParams.get("novo") != null && podeLancar) { setTipoNovo("ENTRADA"); setPainel("novo"); }
    else if (searchParams.get("ofx") != null && podeImportar) setPainel("ofx");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const carregar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = {};
      if (filtroTipo === "ENTRADA" || filtroTipo === "SAIDA") params.tipo = filtroTipo;
      const payload = await listarLancamentos(request, params);
      let list = Array.isArray(payload) ? payload : payload?.results || [];
      if (filtroTipo === "CONCILIAR") list = list.filter((l) => !l.conciliado);
      setItems(list);
      setPagina(0);
      setSelecionados(new Set());
    } catch (err) {
      setError(err?.message || "Erro ao carregar lançamentos.");
      setItems([]);
    } finally { setLoading(false); }
  }, [request, filtroTipo]);

  useEffect(() => { carregar(); }, [carregar]);

  // Busca client-side (sobre o conjunto carregado)
  const filtrados = useMemo(() => {
    const t = busca.trim().toLowerCase();
    if (!t) return items;
    return items.filter((l) =>
      `${l.descricao} ${l.categoria_nome} ${l.valor} ${l.numero}`.toLowerCase().includes(t));
  }, [items, busca]);

  const stats = useMemo(() => {
    let entradas = 0, saidas = 0, conc = 0, aConc = 0;
    for (const l of items) {
      const v = Number(l.valor || 0);
      if (l.tipo === "ENTRADA") entradas += v; else saidas += v;
      if (l.conciliado) conc += 1;
      if (l.confirmado && !l.conciliado) aConc += 1;
    }
    return { entradas, saidas, conc, total: items.length, aConc };
  }, [items]);

  const totalPaginas = Math.max(1, Math.ceil(filtrados.length / PAGE));
  const pagItems = filtrados.slice(pagina * PAGE, pagina * PAGE + PAGE);

  const toggleSel = (id) => setSelecionados((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const fecharPainel = () => {
    setPainel(null);
    if (searchParams.has("novo") || searchParams.has("ofx")) setSearchParams({}, { replace: true });
  };

  const handleConciliar = async (l) => {
    await conciliarLancamento(request, l.id, { conciliado: !l.conciliado });
    await carregar();
  };
  const handleConfirmar = async (l) => {
    await confirmarLancamento(request, l.id, { data_caixa: new Date().toISOString().slice(0, 10) });
    await carregar();
  };

  const TABS = [
    { id: "", label: "Todos" },
    { id: "ENTRADA", label: "Entradas" },
    { id: "SAIDA", label: "Saídas" },
    { id: "CONCILIAR", label: "A conciliar" },
  ];

  return (
    <PageContainer style={{ maxWidth: "none" }} className="fin2">
      {/* Header */}
      <div className="flex between" style={{ alignItems: "flex-end", marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div className="breadcrumb">
            <span>Início</span><span className="crumb-sep">/</span>
            <span>Financeiro</span><span className="crumb-sep">/</span>
            <span className="active">Lançamentos</span>
          </div>
          <h1 className="page-title">Lançamentos</h1>
          <div className="page-sub">
            {stats.total} no período · {stats.aConc} a conciliar
          </div>
        </div>
        <div className="flex gap-2" style={{ flexWrap: "wrap" }}>
          {podeImportar && (
            <button type="button" className="btn btn--ghost" onClick={() => setPainel("ofx")}>
              <FiDownload size={14} aria-hidden="true" /> Importar OFX
            </button>
          )}
          <button type="button" className="btn btn--secondary" onClick={() => navigate("/financeiro/conciliacao-bancaria")}>
            Conciliar
          </button>
          {podeLancar && (
            <button type="button" className="btn btn--primary"
                    onClick={() => { setTipoNovo("ENTRADA"); setPainel("novo"); }}>
              <FiPlus size={14} aria-hidden="true" /> Novo lançamento
            </button>
          )}
        </div>
      </div>

      {/* Stat cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 14 }}>
        <div className="cmp" style={{ padding: "14px 16px" }}>
          <div className="tiny mono muted" style={{ marginBottom: 6 }}>ENTRADAS · PERÍODO</div>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 22, letterSpacing: "-0.025em", color: "var(--success-text)" }}>{formatarReais(stats.entradas)}</div>
        </div>
        <div className="cmp" style={{ padding: "14px 16px" }}>
          <div className="tiny mono muted" style={{ marginBottom: 6 }}>SAÍDAS · PERÍODO</div>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 22, letterSpacing: "-0.025em", color: "var(--danger-text)" }}>{formatarReais(stats.saidas)}</div>
        </div>
        <div className="cmp" style={{ padding: "14px 16px" }}>
          <div className="tiny mono muted" style={{ marginBottom: 6 }}>CONCILIADOS</div>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 22, letterSpacing: "-0.025em" }}>{stats.conc} <span style={{ fontSize: 12, color: "var(--ink-3)", fontWeight: 500 }}>/ {stats.total}</span></div>
        </div>
        <div className="cmp" style={{ padding: "14px 16px", borderColor: "var(--warning-border)", background: "var(--warning-bg)" }}>
          <div className="tiny mono" style={{ marginBottom: 6, color: "var(--warning-text)" }}>A CONCILIAR</div>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 22, letterSpacing: "-0.025em", color: "var(--warning-text)" }}>{stats.aConc}</div>
        </div>
      </div>

      {error && <div className="empty"><div className="msg">{error}</div></div>}

      {/* Tabela */}
      <div className="table-wrap">
        <div className="flex between" style={{ padding: "12px 18px", borderBottom: "1px solid var(--border)", gap: 12, flexWrap: "wrap" }}>
          <div className="flex gap-2 items-center" style={{ flexWrap: "wrap" }}>
            <div className="input-prefixed" style={{ height: 32, minWidth: 260 }}>
              <FiSearch size={14} aria-hidden="true" />
              <input value={busca} onChange={(e) => { setBusca(e.target.value); setPagina(0); }}
                     placeholder="Buscar histórico, valor, categoria…" />
            </div>
            <div className="tabchip-group">
              {TABS.map((t) => (
                <div key={t.id || "all"} className={"tabchip" + (filtroTipo === t.id ? " active" : "")}
                     role="button" tabIndex={0} onClick={() => setFiltroTipo(t.id)}
                     onKeyDown={(e) => e.key === "Enter" && setFiltroTipo(t.id)}>{t.label}</div>
              ))}
            </div>
          </div>
          <span className="tiny mono muted">{filtrados.length} RESULTADOS</span>
        </div>

        <table className="dtable">
          <colgroup>
            <col style={{ width: 42 }} /><col style={{ width: 86 }} /><col style={{ width: "32%" }} />
            <col style={{ width: 130 }} /><col style={{ width: 140 }} /><col style={{ width: 120 }} />
            <col style={{ width: 110 }} /><col style={{ width: 110 }} />
          </colgroup>
          <thead>
            <tr>
              <th className="cb"><input type="checkbox" className="rowcheck"
                  checked={pagItems.length > 0 && pagItems.every((l) => selecionados.has(l.id))}
                  onChange={(e) => setSelecionados((prev) => {
                    const next = new Set(prev);
                    pagItems.forEach((l) => (e.target.checked ? next.add(l.id) : next.delete(l.id)));
                    return next;
                  })} /></th>
              <th>Data</th><th>Histórico</th><th>Categoria</th><th>Centro custo</th>
              <th className="num" style={{ textAlign: "right" }}>Valor</th><th>Status</th>
              {(podeLancar || podeConciliar) && <th />}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} style={{ padding: 24, textAlign: "center" }} className="muted">Carregando…</td></tr>}
            {!loading && pagItems.length === 0 && (
              <tr><td colSpan={8} style={{ padding: 0 }}>
                <div className="empty"><div className="msg">Nenhum lançamento.</div></div>
              </td></tr>
            )}
            {pagItems.map((l) => {
              const entrada = l.tipo === "ENTRADA";
              const aConciliar = l.confirmado && !l.conciliado;
              return (
                <tr key={l.id} style={aConciliar ? { background: "var(--warning-bg)" } : undefined}>
                  <td className="cb"><input type="checkbox" className="rowcheck"
                      checked={selecionados.has(l.id)} onChange={() => toggleSel(l.id)} /></td>
                  <td className="mono">{dataCurta(l.data_competencia)}</td>
                  <td className="ell" title={l.descricao}>
                    {l.descricao}{l.pedido_numero ? <span className="muted"> · {l.pedido_numero}</span> : null}
                  </td>
                  <td><span className="badge neutral no-dot" style={{ fontSize: 11 }}>{l.categoria_nome || "Sem categ."}</span></td>
                  <td className="ell">{l.centro_custo_nome || <span className="muted">—</span>}</td>
                  <td className="num" style={{ textAlign: "right", color: entrada ? "var(--success-text)" : "var(--danger-text)", fontWeight: 600 }}>
                    {entrada ? "+" : "−"}{formatarReais(l.valor)}
                  </td>
                  <td>
                    {!l.confirmado ? <span className="badge warning">Previsão</span>
                      : l.conciliado ? <span className="badge success no-dot">Conciliado</span>
                      : <span className="badge warning">A conciliar</span>}
                  </td>
                  {(podeLancar || podeConciliar) && (
                    <td>
                      <div className="flex gap-2">
                        {podeLancar && (
                          <button type="button" className="btn btn-ghost btn-sm"
                                  onClick={() => setEditando(l)}
                                  style={{ padding: "4px 8px", fontSize: 12 }}>Editar</button>
                        )}
                        {!l.confirmado && podeLancar && (
                          <AsyncButton variant="secondary" onClick={() => handleConfirmar(l)}
                                       successMessage="Confirmado" style={{ padding: "4px 8px", fontSize: 12 }}>Confirmar</AsyncButton>
                        )}
                        {l.confirmado && podeConciliar && (
                          <AsyncButton variant="secondary" onClick={() => handleConciliar(l)}
                                       successMessage={l.conciliado ? "Desconciliado" : "Conciliado"}
                                       style={{ padding: "4px 8px", fontSize: 12 }}>
                            {l.conciliado ? "Desconciliar" : "Conciliar"}</AsyncButton>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="flex between" style={{ padding: "12px 18px", borderTop: "1px solid var(--border)", fontSize: 12 }}>
          <span className="muted">
            {filtrados.length === 0 ? "0 resultados"
              : `Mostrando ${pagina * PAGE + 1}–${Math.min((pagina + 1) * PAGE, filtrados.length)} de ${filtrados.length}`}
          </span>
          <div className="flex gap-2">
            <button type="button" className="btn btn--ghost btn--sm" disabled={pagina === 0}
                    onClick={() => setPagina((p) => Math.max(0, p - 1))}>← Anterior</button>
            <button type="button" className="btn btn--ghost btn--sm" disabled={pagina >= totalPaginas - 1}
                    onClick={() => setPagina((p) => Math.min(totalPaginas - 1, p + 1))}>Próxima →</button>
          </div>
        </div>
      </div>

      {/* Bulk bar */}
      {selecionados.size > 0 && (
        <div className="bulk-bar">
          <span className="bulk-count">{selecionados.size} selecionado(s)</span>
          <div className="bulk-actions">
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => setSelecionados(new Set())}>Limpar</button>
          </div>
        </div>
      )}

      <NovoLancamentoPanel open={painel === "novo"} onClose={fecharPainel}
                           onSaved={carregar} tipoInicial={tipoNovo} />
      <NovoLancamentoPanel open={editando != null} lancamento={editando}
                           onClose={() => setEditando(null)} onSaved={carregar} />
      <ImportarOfxPanel open={painel === "ofx"} onClose={fecharPainel} onImported={carregar} />
    </PageContainer>
  );
}
